/* eslint-disable max-lines-per-function */
import { useState, useRef } from 'react';

interface UseFileUploadOptions {
  maxSize?: number;
  onFileSelect?: (file: File | null) => void;
}

type FileValidationError = {
  key: string;
  params?: Record<string, string>;
};

const maxFileSize = 2 * 1024 * 1024; // 2MB
const acceptedImageTypes = ['image/jpeg', 'image/png', 'image/gif'] as const;
const bytesInMb = 1024 * 1024;

const isValidImageType = (
  fileType: string,
  allowedTypes: readonly string[],
): boolean => allowedTypes.includes(fileType);

const formatFileSize = (bytes: number): string => {
  const mb = bytes / bytesInMb;
  return mb % 1 === 0 ? mb.toFixed(0) : mb.toFixed(1);
};

const validateFile = (
  file: File,
  maxSize: number,
  allowedTypes: readonly string[],
): FileValidationError[] => {
  const errors: FileValidationError[] = [];

  if (!isValidImageType(file.type, allowedTypes)) {
    errors.push({ key: 'domain.termine.upload.error.fileType' });
  }
  if (file.size > maxSize) {
    errors.push({
      key: 'domain.termine.upload.error.size',
      params: { maxFileSize: formatFileSize(maxSize) },
    });
  }
  return errors;
};
export function useFileUpload({
  maxSize = maxFileSize,
  onFileSelect,
}: UseFileUploadOptions = {}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<FileValidationError[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = (selectedFile: File) => {
    const validationErrors = validateFile(
      selectedFile,
      maxSize,
      acceptedImageTypes,
    );

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setPreviewUrl(null);
      onFileSelect?.(null);
      return false;
    }

    setErrors([]);

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(URL.createObjectURL(selectedFile));
    onFileSelect?.(selectedFile);

    return true;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) processFile(droppedFile);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleRemove = (e?: React.MouseEvent) => {
    e?.stopPropagation();

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(null);
    setErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onFileSelect?.(null);
  };

  return {
    previewUrl,
    errors,
    isDragOver,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleClick,
    handleInputChange,
    handleRemove,
  };
}
